import {
    listEntradas,
    crearEntrada,
    actualizarEntrada,
    setEntradaVisible,
    borrarEntrada,
    listBusquedasPendientes,
    marcarBusquedasRevisadas,
} from '../infrastructure/faq.repository.js';
import { agruparBusquedas } from '../shared/faq-search-policy.js';
import { GENERIC_ERROR_MESSAGE } from './staff-faq.service.js';

/**
 * Admin side of the FAQ (openspec/changes/staff-faq/spec.md B5, B6): edits
 * entries and reviews failed searches. Validates before touching the
 * repository and returns `{ ok, kind, mensaje }`, like the other panel services.
 */

/** Required fields (spec D4) and the message shown when one is blank. */
const REQUERIDOS = {
    pregunta: 'Escribí la pregunta.',
    en_corto: 'Escribí la respuesta "En corto".',
    categoria: 'Elegí o escribí una categoría.',
};

/** @param {unknown} v */
const texto = (v) => String(v ?? '').trim();

/** Blank optional text → null. @param {unknown} v */
const opcional = (v) => texto(v) || null;

/** Textarea "one per line" → array, blank lines dropped. @param {unknown} v */
const lineas = (v) =>
    String(v ?? '')
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

/** @param {unknown} err */
function fallo(err) {
    window.Sentry?.captureException(err);
    return { ok: false, kind: 'error', mensaje: GENERIC_ERROR_MESSAGE };
}

/**
 * @returns {Promise<{ ok: true, entradas: Array<Record<string, any>> } | { ok: false, kind: 'error', mensaje: string }>}
 */
export async function loadEntradas() {
    try {
        return { ok: true, entradas: await listEntradas() };
    } catch (err) {
        return fallo(err);
    }
}

/**
 * Creates (no `id`) or updates (`id`) an entry from the admin form.
 * @param {{ id?: string, pregunta: string, en_corto: string, categoria: string, ojo?: string, avisa_a?: string, pasos?: string, otras_formas?: string }} form
 * @returns {Promise<{ ok: true, entrada: Record<string, any> } | { ok: false, kind: 'validacion', campo: string, mensaje: string } | { ok: false, kind: 'error', mensaje: string }>}
 */
export async function guardarEntrada(form) {
    for (const [campo, mensaje] of Object.entries(REQUERIDOS)) {
        if (!texto(form[campo])) return { ok: false, kind: 'validacion', campo, mensaje };
    }
    const datos = {
        pregunta: texto(form.pregunta),
        en_corto: texto(form.en_corto),
        categoria: texto(form.categoria),
        ojo: opcional(form.ojo),
        avisa_a: opcional(form.avisa_a),
        pasos: lineas(form.pasos),
        otras_formas: lineas(form.otras_formas),
    };
    try {
        const entrada = form.id ? await actualizarEntrada(form.id, datos) : await crearEntrada(datos);
        return { ok: true, entrada };
    } catch (err) {
        return fallo(err);
    }
}

/**
 * @param {string} id
 * @param {boolean} visible
 * @returns {Promise<{ ok: true } | { ok: false, kind: 'error', mensaje: string }>}
 */
export async function cambiarVisibilidad(id, visible) {
    try {
        await setEntradaVisible(id, visible);
        return { ok: true };
    } catch (err) {
        return fallo(err);
    }
}

/**
 * @param {string} id
 * @returns {Promise<{ ok: true } | { ok: false, kind: 'error', mensaje: string }>}
 */
export async function eliminarEntrada(id) {
    try {
        await borrarEntrada(id);
        return { ok: true };
    } catch (err) {
        return fallo(err);
    }
}

/**
 * Pending failed searches grouped by normalized query (B6). `truncado`: only
 * the newest pending rows were loaded.
 * @returns {Promise<{ ok: true, grupos: ReturnType<typeof agruparBusquedas>, truncado: boolean } | { ok: false, kind: 'error', mensaje: string }>}
 */
export async function loadBusquedasPendientes() {
    try {
        const { filas, truncado } = await listBusquedasPendientes();
        return { ok: true, grupos: agruparBusquedas(filas), truncado };
    } catch (err) {
        return fallo(err);
    }
}

/**
 * Marks every pending row of a group as reviewed (its loaded ids, plus rows
 * beyond the loaded page with the same raw text).
 * @param {{ ids: string[], consultas: string[] }} grupo
 * @returns {Promise<{ ok: true } | { ok: false, kind: 'error', mensaje: string }>}
 */
export async function marcarRevisadas({ ids, consultas }) {
    try {
        await marcarBusquedasRevisadas({ ids, consultas });
        return { ok: true };
    } catch (err) {
        return fallo(err);
    }
}
